import { useToast } from "../../../../components/common/toasts/ToastContext";
import ErrorToast from "../../../../components/common/toasts/ErrorToast";
import React, { useRef, useState } from "react";
import PremiumBackLink from "../PremiumBackLink";
import { premiumPaths } from "../../navigation";
import PluginSettings from "../PluginSettings";
import PluginSelector from "../plugins/PluginSelector";
import CompanyProfileFields from "./CompanyProfileFields";
import CompanyIssuanceFields from "./CompanyIssuanceFields";
import { premiumRequest } from "../../../../services/Premium";
import { Check } from "../Fields";
import useUnsavedChanges from "../../../../hooks/useUnsavedChanges";
import useAsyncAction from "../../hooks/useAsyncAction";

/** Wspólne pola i zapis firmy; strony dodawania i edycji obsługują wynik operacji. */
export default function CompanyForm({ company, options, token, onSaved }) {
  const { notify } = useToast();
  const [name, setName] = useState(company?.name || "");
  const [active, setActive] = useState(company?.active ?? true);
  const [config, setConfig] = useState(
    company?.config || options.defaultConfig,
  );
  const plugin = options.plugins.find((item) => item.code === config.adapter);
  const { busy, error, run } = useAsyncAction();
  const snapshot = JSON.stringify({ name, active, config });
  const baseline = useRef(snapshot);
  const markClean = useUnsavedChanges(snapshot !== baseline.current);
  const updateConfig = (changes) =>
    setConfig((current) => ({ ...current, ...changes }));
  const save = (event) => {
    event.preventDefault();
    run(async (isActive) => {
      const result = await premiumRequest(
        token,
        company ? `/organizations/${company.id}` : "/organizations",
        {
          method: company ? "PUT" : "POST",
          data: { name, active, config, version: company?.version },
        },
      );
      if (isActive()) {
        baseline.current = snapshot;
        markClean();
        notify(
          "Zapisano ustawienia firmy. Dostęp pracowników znajdziesz w zakładce „Użytkownicy”.",
        );
        onSaved(result.organization);
      }
    });
  };
  return (
    <form onSubmit={save}>
      <section className="card premium-panel">
        <div className="premium-toolbar premium-settings-header">
          <h2>{company ? "Ustawienia firmy" : "Dodaj firmę Premium"}</h2>
          <Check
            label="Firma ma aktywny dostęp Premium"
            checked={active}
            disabled={busy}
            onChange={(event) => setActive(event.target.checked)}
          />
        </div>
        <p className="mb-0">
          Ustawienia dotyczą nowych zleceń. Istniejące zlecenia zachowują
          ustawienia z chwili utworzenia.
        </p>
      </section>
      <ErrorToast message={error} />
      <fieldset disabled={busy}>
        <CompanyProfileFields
          name={name}
          config={config}
          onNameChange={setName}
          onConfigChange={updateConfig}
        />
        <CompanyIssuanceFields
          config={config}
          chambers={options.chambers}
          token={token}
          disabled={busy}
          onChange={updateConfig}
        />
        <section className="card premium-panel">
          <PluginSelector
            plugins={options.plugins}
            value={config.adapter}
            onChange={(selected) =>
              setConfig((current) => ({
                ...Object.fromEntries(
                  Object.keys(options.commonDefaults).map((key) => [
                    key,
                    current[key],
                  ]),
                ),
                adapter: selected.code,
                ...Object.fromEntries(
                  selected.settings.map((setting) => [
                    setting.key,
                    setting.default,
                  ]),
                ),
              }))
            }
          />
        </section>
        {plugin && (
          <PluginSettings
            settings={plugin.settings}
            config={config}
            onChange={(key, value) => updateConfig({ [key]: value })}
          />
        )}
        <div className="premium-form-actions">
          {!company && (
            <PremiumBackLink to={premiumPaths.admin} destination="Lista firm" />
          )}
          <button className="btn btn-primary">
            {busy
              ? "Zapisywanie…"
              : company
                ? "Zapisz ustawienia"
                : "Utwórz firmę"}
          </button>
        </div>
      </fieldset>
    </form>
  );
}
